import { useMutation, useQueryClient } from '@tanstack/react-query';

import { contactsAPI, contactsEndpoints } from '@/api/contacts';
import type { UpsertContactIdentityPayload } from '@/api/contacts';

interface UpsertIdentityVariables extends UpsertContactIdentityPayload {
  contactId: string;
}

interface DeleteIdentityVariables {
  contactId: string;
  identityId: string;
}

export const useContactIdentitiesMutation = () => {
  const queryClient = useQueryClient();

  const invalidateIdentities = (contactId: string) => {
    queryClient.invalidateQueries({ queryKey: [contactsEndpoints.identities(contactId)] });
  };

  const upsertMutation = useMutation<void, Error, UpsertIdentityVariables>({
    mutationFn: ({ contactId, ...payload }) => contactsAPI.upsertIdentity(contactId, payload),
    onSuccess: (_data, variables) => invalidateIdentities(variables.contactId),
  });

  const deleteMutation = useMutation<void, Error, DeleteIdentityVariables>({
    mutationFn: ({ contactId, identityId }) => contactsAPI.deleteIdentity(contactId, identityId),
    onSuccess: (_data, variables) => invalidateIdentities(variables.contactId),
  });

  return {
    upsertIdentity: upsertMutation.mutateAsync,
    isUpserting: upsertMutation.isPending,
    deleteIdentity: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};
